import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { logError, logWarn } from '../lib/log';

const LanguageSelector = ({ testIdSuffix = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Add try-catch for debugging
  try {
    const { currentLanguage, changeLanguage, languages, currentLangInfo } = useLanguage();
    
    const handleLanguageChange = (langCode) => {
      changeLanguage(langCode);
      setIsOpen(false);
    };
    
    // Add fallback if context is not available
    if (!currentLanguage || !currentLangInfo) {
      logWarn('⚠️ Language context not available, showing fallback');
      return (
        <div className="px-3 py-2 bg-gray-900/50 rounded-lg border border-white/10">
          <span className="text-white text-sm">🇮🇹 IT</span>
        </div>
      );
    }
    
    return (
      <div className="relative">
        {/* Current Language Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          data-testid={`language-selector-button${testIdSuffix}`}
          className="flex items-center gap-2 px-3 py-2 bg-gray-900/50 hover:bg-gray-900/70 rounded-lg transition-colors duration-150 border border-white/10"
        >
          <span className="text-lg">{currentLangInfo.flag}</span>
          <span className="text-white text-sm font-medium hidden md:block">
            {currentLangInfo.code.toUpperCase()}
          </span>
          <svg 
            className={`w-4 h-4 text-white transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {/* Language Dropdown */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown Menu */}
            <div className="absolute top-full right-0 mt-1 w-48 bg-black border border-white/10 rounded-lg shadow-xl z-[100] overflow-hidden">
              {Object.entries(languages).map(([code, lang]) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code)}
                  data-testid={`lang-option-${code}${testIdSuffix}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-900/50 transition-colors duration-150 ${
                    currentLanguage === code 
                      ? 'bg-pink-600/20 text-pink-300' 
                      : 'text-white hover:text-pink-300'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="font-medium">{lang.name}</span>
                  {currentLanguage === code && (
                    <span className="ml-auto text-pink-400">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
    
  } catch (error) {
    logError('❌ LanguageSelector error:', error);
    // Return fallback component
    return (
      <div className="px-3 py-2 bg-red-900/50 rounded-lg border border-red-500/30">
        <span className="text-red-300 text-sm">Lang Error</span>
      </div>
    );
  }
};

export default LanguageSelector;